import type { BrandMergeGroup } from '@/types/database-schema';
import { buildConfiguredBrandGroups, resolveTrackedBrand } from '@/utils/brandMatching';

export type DataExplorerModuleMetadata = {
  metrics: readonly string[];
  keywords: readonly string[];
  capabilities: readonly string[];
  toolNames: readonly string[];
};

// Curated from mcp/insights-tools.ts and the corresponding page components.
// Keep this catalog client-safe: importing the server tool graph pulls in server dependencies.
// toolNames record provenance; metrics and capabilities are the human-facing labels.
const MODULE_METADATA: Readonly<Record<string, DataExplorerModuleMetadata>> = {
  '/app/workspace?new_chat=true': {
    metrics: [],
    keywords: ['notebook', 'assistant', 'new chat', 'research', 'workspace'],
    capabilities: ['Ask a question', 'Continue research conversations'],
    toolNames: ['notebook.ask', 'notebook.get_session'],
  },
  '/app/ai-search/competitors': {
    metrics: ['Share of voice', 'Visibility', 'Top-3 share of voice', 'Top-3 rate', '#1 rate'],
    keywords: ['SOV', 'competitors', 'brand mentions', 'leaderboard', 'answer presence'],
    capabilities: ['Compare brands', 'Find market leaders by AI engine and buyer question'],
    toolNames: ['brands.leaderboard'],
  },
  '/app/ai-search/competitors/charts': {
    metrics: ['Share of voice over time', 'Visibility over time', 'Top-3 rate over time'],
    keywords: ['SOV trends', 'competitor trends', 'brand momentum', 'market history'],
    capabilities: ['Compare brand trends', 'Track market changes across collection periods'],
    toolNames: ['brands.leaderboard'],
  },
  '/app/visibility': {
    metrics: ['Visibility', 'Answer presence', 'Visible prompts'],
    keywords: [
      'brand mentions',
      'AI visibility',
      'brand exposure',
      'discovery',
      'decision',
      'trust',
    ],
    capabilities: ['Explore brand presence by AI engine, stage, and prompt'],
    toolNames: ['visibility.summary', 'visibility.detail'],
  },
  '/app/rank': {
    metrics: ['Rank', 'Top-3 placements', '#1 placements'],
    keywords: ['rankings', 'brand position', 'competitor comparison', 'leaderboard'],
    capabilities: ['Compare brand rankings across tracked questions and AI engines'],
    toolNames: ['rank.summary'],
  },
  '/app/rank-tables': {
    metrics: ['Rank by prompt', 'Rank by engine'],
    keywords: ['ranking details', 'rank table', 'brand position', 'question by question'],
    capabilities: ['Inspect individual ranking results by prompt and AI engine'],
    toolNames: ['rank.detail'],
  },
  '/app/sentiment': {
    metrics: ['Positioning terms', 'Use cases', 'Strengths', 'Weaknesses'],
    keywords: ['brand perception', 'market language', 'claims', 'messaging', 'reputation'],
    capabilities: ['Explore how AI describes your brand', 'Compare language signals over time'],
    toolNames: ['sentiment.summary'],
  },
  '/app/ads': {
    metrics: [],
    keywords: ['paid search', 'advertising', 'campaigns', 'ad performance'],
    capabilities: ['Review connected advertising sources and paid search performance'],
    toolNames: ['ads.sources', 'ads.summary'],
  },
  '/app/weekly-report': {
    metrics: ['Visibility', 'Top-3 placements', 'Answers collected', 'Cited domains'],
    keywords: ['weekly summary', 'week over week', 'what changed', 'presentation', 'PDF'],
    capabilities: ['Review weekly market movement', 'Present and export the weekly report'],
    toolNames: ['visibility.summary', 'rank.summary', 'citations.summary', 'sentiment.summary'],
  },
  '/app/citation-report': {
    metrics: ['Citations', 'Cited domains', 'Cited pages'],
    keywords: ['citation sources', 'source authority', 'publishers', 'domain coverage'],
    capabilities: ['Discover the domains and publishers cited in AI answers'],
    toolNames: ['citations.summary'],
  },
  '/app/citation-report/charts': {
    metrics: ['Citation trends', 'Source coverage over time'],
    keywords: ['source trends', 'citation history', 'publisher trends', 'domain trends'],
    capabilities: ['Compare how cited sources change across collection periods'],
    toolNames: ['citations.summary', 'citations.rows'],
  },
  '/app/channel-manager': {
    metrics: ['Opportunity pages', 'Brand mention pages'],
    keywords: ['outreach', 'contacts', 'publisher channels', 'editorial', 'earned media'],
    capabilities: ['Find publisher opportunities', 'Review contact routes and outreach status'],
    toolNames: ['channel_manager.summary', 'channel_manager.detail'],
  },
  '/app/pages/overview': {
    metrics: ['Website traffic', 'Search impressions', 'Search clicks'],
    keywords: ['analytics dashboard', 'website performance', 'source coverage', 'data freshness'],
    capabilities: ['Review connected website analytics', 'Compare traffic and search trends'],
    toolNames: ['website.performance', 'analytics.summary', 'analytics.sources'],
  },
  '/app/pages/readiness': {
    metrics: ['Agent readiness score', 'Layer scores', 'Check coverage'],
    keywords: ['AI agents', 'agent check', 'discoverability', 'crawlability', 'readiness'],
    capabilities: ['Assess whether agents can discover, understand, and act on your site'],
    toolNames: ['readiness.run'],
  },
  '/app/pages': {
    metrics: ['SEO grade', 'HTTP status', 'Citation coverage'],
    keywords: ['page inventory', 'website map', 'crawl health', 'index health', 'AEO'],
    capabilities: ['Browse website pages and folders', 'Inspect page content and SEO health'],
    toolNames: ['website.map', 'pages.summary', 'pages.search', 'pages.read'],
  },
  '/app/pages/review': {
    metrics: [],
    keywords: ['page messaging', 'content review', 'buyer evidence', 'page copy', 'callouts'],
    capabilities: ['Review page messaging against search, AI, and behavior evidence'],
    toolNames: ['page_review.list', 'page_review.get', 'page_review.queue'],
  },
  '/app/pages/analytics/ga4': {
    metrics: ['Sessions', 'Conversions', 'Revenue', 'AI referrals'],
    // Discovery alias only: this report exposes conversion counts, not a conversion-rate KPI.
    keywords: [
      'Google Analytics',
      'GA4',
      'conversion rate',
      'visits',
      'engagement',
      'chatbot traffic',
    ],
    capabilities: ['Explore acquisition channels and landing pages', 'Measure AI referral impact'],
    toolNames: ['analytics.pageMetrics', 'analytics.channelMetrics', 'website.performance'],
  },
  '/app/pages/analytics/gsc': {
    metrics: [
      'Impressions',
      'Clicks',
      'Click-through rate (CTR)',
      'Average position',
      'Tracked queries',
    ],
    keywords: ['Google Search Console', 'GSC', 'organic search', 'search demand', 'keywords'],
    capabilities: ['Explore Google search queries and landing pages'],
    toolNames: ['analytics.queryMetrics', 'analytics.pageMetrics'],
  },
  '/app/pages/analytics/bing': {
    metrics: [
      'Impressions',
      'Clicks',
      'Click-through rate (CTR)',
      'Average position',
      'Tracked queries',
    ],
    keywords: ['Bing Webmaster Tools', 'Microsoft search', 'organic search', 'search demand'],
    capabilities: ['Explore Bing search queries and landing pages'],
    toolNames: ['analytics.queryMetrics', 'analytics.pageMetrics'],
  },
  '/app/pages/analytics/clarity': {
    metrics: ['Rage clicks', 'Dead clicks', 'Scroll depth', 'Quickbacks', 'Errors'],
    keywords: ['Microsoft Clarity', 'UX', 'user behavior', 'friction', 'usability', 'scrolling'],
    capabilities: ['Find pages with user experience friction', 'Prioritize behavior issues'],
    toolNames: ['analytics.uxSignals'],
  },
  '/app/pages/analytics/cloudflare': {
    metrics: ['AI crawler requests', 'Blocked requests', 'Active crawlers', 'Visits', 'Page views'],
    keywords: [
      'AI crawler traffic',
      'bot traffic',
      'GPTBot',
      'OpenAI',
      'Anthropic',
      'AI referrers',
    ],
    capabilities: ['Inspect crawler activity by page and operator', 'Explore AI referrer hosts'],
    toolNames: ['analytics.aiCrawlers', 'website.performance'],
  },
  '/app/revenue': {
    metrics: ['Total sales', 'Orders', 'Average order value', 'Net items sold'],
    keywords: ['Shopify', 'ecommerce', 'revenue attribution', 'AOV', 'customer mix'],
    capabilities: [
      'Compare sales and marketing channels',
      'Explore revenue sources and top products',
    ],
    toolNames: ['shopify.analytics.query'],
  },
  '/app/prompts': {
    metrics: ['Tracked questions', 'Prompt visibility'],
    keywords: ['buyer questions', 'prompt groups', 'topics', 'collection cadence'],
    capabilities: ['Explore tracked buyer questions', 'Manage prompts and groups'],
    toolNames: ['prompts.summary', 'prompts.detail', 'prompts.tracking'],
  },
  '/app/fan-outs': {
    metrics: ['Fan-out frequency'],
    keywords: ['related questions', 'query expansion', 'search paths', 'fanout queries'],
    capabilities: ['Explore the searches AI uses to build answers'],
    toolNames: ['prompts.detail'],
  },
  '/app/citations': {
    metrics: ['Citation count', 'Citation frequency'],
    keywords: ['cited URLs', 'cited pages', 'source content', 'brand mentions'],
    capabilities: ['Inspect cited pages and citation evidence by question and engine'],
    toolNames: ['citations.rows', 'citations.content'],
  },
  '/app/answers': {
    metrics: [],
    keywords: ['raw responses', 'answer text', 'ChatGPT', 'Gemini', 'Google AI Overviews'],
    capabilities: ['Read collected AI answers by prompt, engine, and period'],
    toolNames: ['answers.read'],
  },
};

const EMPTY_METADATA: DataExplorerModuleMetadata = {
  metrics: [],
  keywords: [],
  capabilities: [],
  toolNames: [],
};

export function getDataExplorerModuleMetadata(href: string): DataExplorerModuleMetadata {
  return MODULE_METADATA[href] ?? EMPTY_METADATA;
}

const QUESTION_WORDS = new Set([
  'about',
  'dashboard',
  'dashboards',
  'get',
  'give',
  'looking',
  'know',
  'track',
  'tracking',
  'metric',
  'metrics',
  'module',
  'modules',
  'showing',
  'shows',
  'view',
  'a',
  'an',
  'and',
  'are',
  'at',
  'be',
  'can',
  'could',
  'do',
  'does',
  'find',
  'for',
  'from',
  'how',
  'i',
  'in',
  'is',
  'it',
  'me',
  'my',
  'need',
  'of',
  'on',
  'our',
  'please',
  'see',
  'show',
  'some',
  'tell',
  'that',
  'the',
  'these',
  'this',
  'to',
  'want',
  'we',
  'what',
  'where',
  'which',
  'with',
  'would',
  'you',
  'your',
]);

function tokenize(text: string): string[] {
  return text
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(word => word && !QUESTION_WORDS.has(word))
    .map(word => {
      if (word.length > 4 && word.endsWith('ies')) return `${word.slice(0, -3)}y`;
      if (word.length > 3 && word.endsWith('s') && !/(ss|us|is|ics)$/.test(word)) {
        return word.slice(0, -1);
      }
      return word;
    });
}

type SearchableModule = {
  href: string;
  label: string;
  description?: string;
  section: string;
  searchTerms?: readonly string[];
};

/** One insertion, deletion, substitution, or adjacent transposition. Short tokens stay exact. */
function isNearbyWord(left: string, right: string): boolean {
  if (left.length < 4 || right.length < 4 || Math.abs(left.length - right.length) > 1) return false;
  let i = 0;
  while (i < left.length && left[i] === right[i]) i++;
  if (i === left.length) return right.length - left.length <= 1;
  if (left.length === right.length) {
    return (
      (left.length >= 5 && left.slice(i + 1) === right.slice(i + 1)) ||
      (left[i] === right[i + 1] &&
        left[i + 1] === right[i] &&
        left.slice(i + 2) === right.slice(i + 2))
    );
  }
  return left.length > right.length
    ? left.slice(i + 1) === right.slice(i)
    : left.slice(i) === right.slice(i + 1);
}

function wordScore(token: string, words: readonly string[]): number {
  if (words.includes(token)) return 1;
  if (token.length >= 2 && words.some(word => word.startsWith(token))) return 0.65;
  if (words.some(word => isNearbyWord(token, word))) return 0.35;
  return 0;
}

type SearchField = { text: string; weight: number; tokens: string[] };
const moduleSearchIndex = new WeakMap<SearchableModule, SearchField[]>();

function getModuleSearchFields(item: SearchableModule) {
  const cached = moduleSearchIndex.get(item);
  if (cached) return cached;
  const metadata = getDataExplorerModuleMetadata(item.href);
  const fields = [
    { text: item.label, weight: 16 },
    ...metadata.metrics.map(text => ({ text, weight: 10 })),
    ...metadata.keywords.map(text => ({ text, weight: 7 })),
    ...(item.searchTerms || []).map(text => ({ text, weight: 7 })),
    ...metadata.capabilities.map(text => ({ text, weight: 5 })),
    { text: item.description ?? '', weight: 3 },
    { text: item.section, weight: 2 },
  ].map(field => ({ ...field, tokens: tokenize(field.text) }));
  moduleSearchIndex.set(item, fields);
  return fields;
}

export type DataExplorerSearchResult<T> = {
  item: T;
  score: number;
  matchedMetrics: string[];
};

/** Search only the caller's available navigation items, preserving their order on ties. */
export function searchDataExplorerModules<T extends SearchableModule>(
  items: readonly T[],
  query: string
): DataExplorerSearchResult<T>[] {
  if (!query.trim()) return items.map(item => ({ item, score: 0, matchedMetrics: [] }));

  const queryTokens = [...new Set(tokenize(query))];
  if (!queryTokens.length) return [];
  const phrase = queryTokens.join(' ');

  return items
    .map((item, index) => {
      const metadata = getDataExplorerModuleMetadata(item.href);
      const fields = getModuleSearchFields(item);

      const tokenScores = queryTokens.map(token =>
        Math.max(0, ...fields.map(field => wordScore(token, field.tokens) * field.weight))
      );
      if (tokenScores.some(score => score === 0)) return null;

      const phraseScore = Math.max(
        0,
        ...fields.map(field => {
          const normalized = field.tokens.join(' ');
          if (normalized === phrase) return field.weight * 6;
          return ` ${normalized} `.includes(` ${phrase} `) ? field.weight * 4 : 0;
        })
      );
      const matchedMetrics = metadata.metrics.filter(metric =>
        queryTokens.some(token => wordScore(token, tokenize(metric)) > 0)
      );

      return {
        item,
        index,
        score: tokenScores.reduce((sum, score) => sum + score, 0) + phraseScore,
        matchedMetrics,
      };
    })
    .filter(result => result !== null)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map(({ item, score, matchedMetrics }) => ({ item, score, matchedMetrics }));
}

export type DataExplorerBrand = {
  slug: string;
  name: string;
  domain: string;
  aliases: readonly string[];
  isOwn: boolean;
  description?: string;
  metrics?: { label: string; value: string }[];
};

export type DataExplorerBrandFavorite = DataExplorerBrand & { href: string };

export function normalizeDataExplorerBrandFavorites(value: unknown): DataExplorerBrandFavorite[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  return value
    .filter((item): item is DataExplorerBrandFavorite => {
      if (
        !item ||
        typeof item !== 'object' ||
        typeof item.slug !== 'string' ||
        !item.slug ||
        typeof item.name !== 'string' ||
        typeof item.domain !== 'string' ||
        typeof item.href !== 'string' ||
        typeof item.isOwn !== 'boolean' ||
        !Array.isArray(item.aliases) ||
        !item.aliases.every((alias: unknown) => typeof alias === 'string')
      )
        return false;
      const expectedPath = `/app/ai-search/${encodeURIComponent(item.slug)}`;
      if (item.href.split('?')[0] !== expectedPath || seen.has(item.slug)) return false;
      seen.add(item.slug);
      return true;
    })
    .slice(0, 100);
}

function brandSearchText(value: string) {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

export function buildDataExplorerBrands(
  discovered: readonly {
    brand: string;
    brand_lowercase: string;
    website?: string | null;
    description?: string | null;
    discovery_visibility?: number | string;
    visibility_pct?: number | string;
    mention_count?: number;
  }[],
  ownBrand?: { name: string; slug?: string; website?: string; aliases?: readonly string[] },
  brandMergeGroups: BrandMergeGroup[] = []
): DataExplorerBrand[] {
  function domain(website?: string | null) {
    if (!website) return '';
    try {
      return new URL(website.includes('://') ? website : `https://${website}`).hostname.replace(
        /^www\./,
        ''
      );
    } catch {
      return '';
    }
  }
  const configuredBrandGroups = buildConfiguredBrandGroups({
    brandName: ownBrand?.name,
    projectBrandSlug: ownBrand?.slug,
    brandVariations: [...(ownBrand?.aliases || [])],
    brandMergeGroups,
  });
  const bySlug = new Map<string, DataExplorerBrand>();
  for (const brand of discovered) {
    const name = brand.brand.trim();
    const slug = brand.brand_lowercase.trim();
    if (!name || !slug || bySlug.has(slug)) continue;
    const matched = resolveTrackedBrand({ brandSlug: slug, configuredBrandGroups });
    const metrics: { label: string; value: string }[] = [];
    if (brand.discovery_visibility != null && Number.isFinite(Number(brand.discovery_visibility))) {
      metrics.push({
        label: 'Visibility',
        value: `${(Number(brand.discovery_visibility) * 100).toFixed(1)}%`,
      });
    }
    if (brand.visibility_pct != null && Number.isFinite(Number(brand.visibility_pct))) {
      metrics.push({
        label: 'Share of voice',
        value: `${Number(brand.visibility_pct).toFixed(1)}%`,
      });
    }
    if (brand.mention_count != null && Number.isFinite(brand.mention_count)) {
      metrics.push({ label: 'Mentions', value: brand.mention_count.toLocaleString() });
    }
    // The leaderboard already groups aliases. Preserve its exact row key and ordering;
    // matching enriches search and the "Your brand" label, never rewrites destinations.
    bySlug.set(slug, {
      name,
      slug,
      domain: domain(brand.website || (matched.isProjectDefaultBrand ? ownBrand?.website : '')),
      aliases: matched.brandVariations,
      isOwn: matched.isProjectDefaultBrand,
      ...(brand.description ? { description: brand.description } : {}),
      metrics,
    });
  }
  return [...bySlug.values()];
}

const brandSearchIndex = new WeakMap<
  DataExplorerBrand,
  { fields: string[]; words: string[]; compactFields: string[] }
>();

export function searchDataExplorerBrands(brands: readonly DataExplorerBrand[], query: string) {
  const normalized = brandSearchText(query);
  if (!normalized) return [...brands];
  const terms = normalized.split(' ');
  const compact = normalized.replaceAll(' ', '');
  return brands
    .map((brand, index) => {
      let entry = brandSearchIndex.get(brand);
      if (!entry) {
        const fields = [brand.name, brand.slug, brand.domain, ...brand.aliases].map(
          brandSearchText
        );
        entry = {
          fields,
          words: [...new Set(fields.flatMap(field => field.split(' ')))],
          compactFields: fields.map(field => field.replaceAll(' ', '')),
        };
        brandSearchIndex.set(brand, entry);
      }
      const scores = terms.map(term => wordScore(term, entry.words));
      const exact = entry.fields.includes(normalized);
      const compactMatch = entry.compactFields.includes(compact);
      if (!compactMatch && scores.some(score => !score)) return null;
      return {
        brand,
        index,
        score: (exact ? 100 : compactMatch ? 80 : 0) + scores.reduce((a, b) => a + b, 0),
      };
    })
    .filter(result => result !== null)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map(result => result.brand);
}
